import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ErrorStrings } from 'src/app/constants/form.constants';
import { Alert } from 'src/app/interfaces/alert';
import { ErrorCode } from 'src/app/interfaces/user';
import { errorMessagesMap } from 'src/app/constants/error-messages';
import {
  AuthManagerService,
  RegisterParams,
} from 'src/app/services/auth-manager/auth-manager.service';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  RedirectionParams,
  RedirectionService,
} from 'src/app/services/redirection/redirection.service';
import { EMAIL_ACTIVATION_LINK_SENT } from 'src/app/constants/app.constants';
import { EMAIL_SENT_ROUTE } from 'src/app/constants/route.constants';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { atLeastOneCheckboxToBeCheckedValidator } from 'src/app/shared/utils/utils';
import { isPlatformBrowser } from '@angular/common';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  loading = false;
  alert: Alert = null;
  hidePassword: boolean = true;
  hideConfirmPassword: boolean = true;
  useRebrandedLayout: boolean;
  errorStrings = ErrorStrings;
  adminLoginUrl = environment.adminLoginUrl;
  redirectParams?: RedirectionParams;
  model: RegisterParams = {
    emailAddress: '',
    password: '',
  };

  validationErrorMessage: string;
  passwordConfirmValue: string;

  public thirdPartyCheckboxStateChanged = false;
  public communicationForm: UntypedFormGroup;
  public isBrowser: boolean;

  constructor(
    public authManager: AuthManagerService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectionService: RedirectionService,
    private _fb: UntypedFormBuilder,
    private featureFlagService: FeatureFlagsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (authManager.isAuthenticated) {
      if (this.redirectParams) {
        this.router.navigate(this.redirectParams.l, {
          queryParams: this.redirectParams.q,
          fragment: this.redirectParams.f,
        });
      } else {
        this.router.navigate(['/dashboard']);
      }
    }
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
  }

  onRegisterClick(): void {
    if (!this.model.password || this.model.password.length < 8) {
      return;
    }
    if (!this.passwordConfirmValue || this.model.password !== this.passwordConfirmValue) {
      return;
    }
    if (!this.model.emailAddress || this.model.emailAddress.length < 5) {
      return;
    }

    if (
      this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
        .errors
    ) {
      this.validationErrorMessage = 'Choose your marketing preferences to register!';
    } else {
      this.validationErrorMessage = null;
    }
  }

  ngOnInit(): void {
    // Prefill email address if "email" is passed as query param
    // (this is set when a redirection from the registration page - 401 status code and error_code "email_not_found")
    if (this.route.snapshot.queryParams.email) {
      if (this.isBase64(this.route.snapshot.queryParams.email)) {
        this.model.emailAddress = atob(this.route.snapshot.queryParams.email);
      } else {
        this.model.emailAddress = this.route.snapshot.queryParams.email;
      }
      this.alert = {
        type: 'info',
        message: `It looks like you haven’t registered your account yet. Please fill in the form to sign up.`,
      };
    }

    if (this.route.snapshot.queryParams.chip) {
      this.alert = {
        type: 'info',
        message: `It looks like chip number ${this.route.snapshot.queryParams.chip} is not registered yet. Please fill in the form to sign up.`,
      };
    }

    this.initCommunicationForm();
    // Setting redirection to be used when after signup we want the user redirected to a specific page
    this.redirectParams = this.redirectionService.getParams(
      this.route.snapshot.queryParams.r
    );
  }

  public initCommunicationForm() {
    this.communicationForm = this._fb.group({
      subscribeToAllInternalCommunication: [false],
      thirdPartySubscribeOptionSelectGroup: new UntypedFormGroup(
        {
          subscribeToAllThirdParty: new UntypedFormControl(false),
          unsubscribeFromAllThirdParty: new UntypedFormControl(false),
        },
        atLeastOneCheckboxToBeCheckedValidator()
      ),
    });
  }

  public onSubscribeOptionChanged(event) {
    this.thirdPartyCheckboxStateChanged = true;
    if (event.target.checked) {
      if (event.target.id == 'subscribeToAllThirdParty') {
        this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
          .get('unsubscribeFromAllThirdParty')
          .setValue(false);
      } else {
        this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
          .get('subscribeToAllThirdParty')
          .setValue(false);
      }
    }

    if (
      !this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
        .errors
    ) {
      this.validationErrorMessage = null;
    } else {
      this.validationErrorMessage = 'Choose your marketing preferences to register!';
    }
  }

  private isBase64(str: string): boolean {
    const regex = /^[A-Za-z0-9+/]*={0,2}$/;
    return regex.test(str);
  }
  
  signUp() {
    this.closeAlert();
    this.loading = true;
    this.trackEvent('signup::clicked');

    let requestContent: RegisterParams = {
      emailAddress: this.model.emailAddress,
      password: this.model.password,
      newsAndUpdatesEmail: !this.communicationForm.get(
        'subscribeToAllInternalCommunication'
      ).value,
      newsAndUpdatesSms: !this.communicationForm.get(
        'subscribeToAllInternalCommunication'
      ).value,
      newsAndUpdatesTelephone: !this.communicationForm.get(
        'subscribeToAllInternalCommunication'
      ).value,
      thirdPartyCommunicationEmail: this.communicationForm.get(
        'thirdPartySubscribeOptionSelectGroup.subscribeToAllThirdParty'
      ).value,
      thirdPartyCommunicationSms: this.communicationForm.get(
        'thirdPartySubscribeOptionSelectGroup.subscribeToAllThirdParty'
      ).value,
    };

    this.authManager
      .register(requestContent)
      .subscribe(
        (response) => {
          // Activation email link sent, tell the user to check their inbox
          if (response == EMAIL_ACTIVATION_LINK_SENT) {
            this.router.navigate([`/${EMAIL_SENT_ROUTE}`]);
          }
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            this.alert = {
              type: 'danger',
              message: 'Please enter a valid email address and a password.',
            };

            if (err.error.error_code === 'invalid_data') {
              this.alert = {
                type: 'danger',
                message: err.error.error_message ?? 'An unexpected error occurred.'
              };
            } else if (err.error?.errorCode === ErrorCode.RegistrationNotAllowed) {
              this.alert = {
                type: 'danger',
                message: errorMessagesMap['registration_not_allowed_error_message']
              };
            }

          } else if (err instanceof HttpErrorResponse && err.status === 409) {
            // User already registered: Redirect to login form
            this.router.navigate(['/login'], {
              queryParams: { email: this.model.emailAddress },
            });
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  closeAlert() {
    this.alert = null;
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }
}
